<template>
	<div>
		<div class="cs-head">
			<div>
				<h1 class="cs-title">Bundle values</h1>
				<p class="cs-sub">A bundle or package is one price for several parts. For each part, write what it is worth inside the bundle and one line on why that differs from the part's own price. This never changes a quote. It shows the margin per part.</p>
			</div>
			<div class="row"><button class="pill" @click="exportCsv"><i class="fa-regular fa-arrow-down-to-line"></i> Export all, CSV</button></div>
		</div>

		<div v-if="loading" class="qblock">Loading the bundles…</div>
		<div v-else class="pe-fin-split">
			<div class="pe-fin-list">
				<template v-for="g in groups">
					<h6 :key="'h' + g.brand" class="bd-grp" style="margin:8px 10px 4px">{{ brandName(g.brand) }}</h6>
					<button v-for="c in g.items" :key="c.id" :class="{ on: sel && sel.id === c.id }" @click="pick(c)"><b>{{ c.name }}</b><span>{{ valued(c) }} of {{ c.parts }} parts valued</span></button>
				</template>
			</div>

			<div v-if="sel" style="min-width:0">
				<div class="qblock" style="margin-top:0">
					<div class="qhead" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
						<h5>{{ sel.name }} <span class="mono">{{ sel.code }}</span></h5>
						<div v-if="sel.bands.length > 1" class="ff" style="margin:0 0 0 auto;min-width:220px"><select v-model.number="bandIdx" aria-label="Population"><option v-for="(b, i) in sel.bands" :key="i" :value="i">{{ b.band ? 'At ' + bandLabel(b.band[0], b.band[1]) + ' residents' : 'Any size' }}</option></select></div>
					</div>
					<div class="cards" style="margin-top:12px">
						<div class="kcard"><h6>List price</h6><div class="pe-fin-big">{{ money(band.list_cents) }}</div><p class="sub-hint">{{ band.band ? 'At ' + bandLabel(band.band[0], band.band[1]) + ' residents' : 'Any size' }}</p></div>
						<div class="kcard"><h6>Parts at their own price</h6><div class="pe-fin-big">{{ money(band.parts_sum_cents) }}</div><p class="sub-hint">{{ band.priced_parts }} with a price, {{ band.unpriced_parts }} without one</p></div>
						<div class="kcard"><h6>Values you wrote</h6><div class="pe-fin-big">{{ money(allocated) }}</div><p class="sub-hint">{{ written }} of {{ parts.length }} parts<template v-if="band.list_cents !== null && written">, {{ gapText }}</template></p></div>
					</div>
				</div>

				<div class="tbl-scroll">
					<table class="tbl nochk pe-fin-sheet">
						<thead><tr><th class="sticky-l2">Part</th><th>Covers</th><th class="r">Own price</th><th class="r">Value $</th><th>Why it differs</th></tr></thead>
						<tbody>
							<tr v-for="p in parts" :key="p.code">
								<td class="sticky-l2 wrapname"><div class="acct"><span><span class="acct-name">{{ p.name }}</span><span class="sub"><span class="mono">{{ p.code }}</span><template v-if="noteShown(p)"> · {{ p.note }}</template></span></span></div></td>
								<td class="faint">{{ p.qty ? 'up to ' + p.qty : 'unlimited' }}</td>
								<td class="r num">{{ p.standalone_cents === null ? 'no own price' : money(p.standalone_cents) }}</td>
								<td class="inp narrow"><input type="number" min="0" step="0.01" placeholder="not set" v-model="edits[p.code].value" :class="{ changed: isChanged(p, 'value') }" :aria-label="'Value of ' + p.code" /></td>
								<td class="inp" style="width:auto;min-width:200px"><input class="txt" type="text" maxlength="2000" placeholder="not set" v-model="edits[p.code].decision" :class="{ changed: isChanged(p, 'decision') }" :aria-label="'Why, ' + p.code" /></td>
							</tr>
						</tbody>
						<tfoot><tr><td colspan="5"><div class="row" style="justify-content:flex-end"><span v-if="changedCount" class="yn warn">{{ changedCount }} not saved</span><button class="pill sm" :disabled="!changedCount || saving" @click="reset">Discard</button><button class="btn-primary" :disabled="!changedCount || saving" @click="save">{{ saving ? 'Saving…' : 'Save values' }}</button></div></td></tr></tfoot>
					</table>
				</div>
				<p class="sub-hint">Own price: the part's list price at this size, times what the bundle covers. A value is one number for every size; the list price above changes with the population you pick.</p>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '../../../api/index.js'
import { money, bandLabel, brandName, errorText, loadLocal, saveLocal } from '../pe-util'

const KEY = 'pricing-bundle-values'
const toCents = v => (v === '' || v === null || v === undefined ? null : Math.round(Number(v) * 100))

export default {
	name: 'PricingBundleValues',
	data() { return { comps: [], sel: null, bandIdx: 0, edits: {}, loading: false, saving: false } },
	computed: {
		groups() {
			const out = {}
			this.comps.forEach(c => { (out[c.brand] = out[c.brand] || { brand: c.brand, items: [] }).items.push(c) })
			return Object.values(out)
		},
		band() { return this.sel ? this.sel.bands[this.bandIdx] || this.sel.bands[0] : null },
		parts() { return this.band ? this.band.parts : [] },
		written() { return this.parts.filter(p => toCents(this.edits[p.code] && this.edits[p.code].value) !== null).length },
		allocated() { return this.parts.reduce((t, p) => t + (toCents(this.edits[p.code] && this.edits[p.code].value) || 0), 0) },
		gapText() { const d = this.band.list_cents - this.allocated; return d === 0 ? 'equal to the list price' : money(Math.abs(d)) + (d > 0 ? ' left to place' : ' over the list price') },
		changedCount() { return this.parts.filter(p => this.isChanged(p, 'value') || this.isChanged(p, 'decision')).length },
	},
	created() { this.load(loadLocal(KEY, null)) },
	beforeRouteLeave(to, from, next) {
		if (!this.changedCount) return next()
		this.$root.$pePrompt({ title: 'Leave without saving?', text: this.changedCount + (this.changedCount === 1 ? ' part is' : ' parts are') + ' not saved.', confirm: 'Leave', danger: true }).then(ok => next(!!ok))
	},
	methods: {
		money, bandLabel, brandName,
		toast(title, icon = 'error') { this.$root.$emit('pe-notice', { tone: icon === 'success' ? 'ok' : (icon === 'info' ? 'info' : 'warn'), text: title }) },
		load(selectId) {
			this.loading = true
			return restApi.get('/pricing/compositions').then(({ data }) => {
				this.comps = data
				const c = data.find(x => x.id === selectId) || data[0]
				if (c) this.pick(c, true)
			}).catch(err => this.toast(errorText(err, 'Could not load the bundles'))).finally(() => { this.loading = false })
		},
		noteShown(p) { return p.note && !p.name.toLowerCase().includes(p.note.toLowerCase()) && !p.note.toLowerCase().includes(p.name.toLowerCase()) },
		valued(c) { return c.bands[0] ? c.bands[0].parts.filter(p => p.value_cents !== null).length : 0 },
		pick(c, force) {
			const go = () => {
				this.sel = c
				saveLocal(KEY, c.id)
				// the band of a town of 5,000 when the bundle has one: the size the deck and the report use
				const i = c.bands.findIndex(b => b.band && b.band[0] <= 5000 && 5000 <= b.band[1])
				this.bandIdx = i === -1 ? 0 : i
				this.reset()
			}
			if (force || !this.changedCount) return go()
			this.$root.$pePrompt({ title: 'Switch without saving?', text: this.changedCount + (this.changedCount === 1 ? ' part is' : ' parts are') + ' not saved.', confirm: 'Switch', danger: true }).then(ok => { if (ok) go() })
		},
		reset() {
			const e = {}
			;(this.sel ? this.sel.bands[0].parts : []).forEach(p => { e[p.code] = { value: p.value_cents === null ? '' : String(p.value_cents / 100), decision: p.decision || '' } })
			this.edits = e
		},
		isChanged(p, f) {
			const e = this.edits[p.code]
			if (!e) return false
			return f === 'value' ? toCents(e.value) !== p.value_cents : (e.decision.trim() || null) !== (p.decision || null)
		},
		save() {
			const includes = this.sel.bands[0].parts.map(p => ({ code: p.code, qty: p.qty || null, note: p.note || null, value_cents: toCents(this.edits[p.code].value), decision: this.edits[p.code].decision.trim() || null }))
			if (includes.some(i => i.value_cents !== null && (isNaN(i.value_cents) || i.value_cents < 0))) return this.toast('A value is not a valid amount')
			this.saving = true
			const id = this.sel.id
			restApi.put('/pricing/skus/' + id, { includes }).then(() => { this.toast('Values saved', 'success'); return this.load(id) })
				.catch(err => this.toast(errorText(err, 'Save failed'))).finally(() => { this.saving = false })
		},
		exportCsv() {
			restApi.get('/pricing/compositions/export', { responseType: 'blob' }).then(res => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' })); a.download = 'pe-compositions.csv'; a.click(); URL.revokeObjectURL(a.href) })
				.catch(err => this.toast(errorText(err, 'Export failed')))
		},
	},
}
</script>
